import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api/api";
import { Button, Card, Form, Row } from "react-bootstrap";
import PageTitle from "../components/common/PageTitle";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import "./AdminManagement.css";

const EMPTY_CREDENTIAL_STATUS = {
  configured: false,
  ready: false,
  source: "none",
  client_id: "",
  secret_configured: false,
  updated_at: null,
};

function formatStatusDate(value, locale) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat(locale || undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

export default function SatelliteManagement() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedField, setSelectedField] = useState("");
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(false);
  const [credentialsStatus, setCredentialsStatus] = useState(EMPTY_CREDENTIAL_STATUS);
  const [credentialsLoading, setCredentialsLoading] = useState(true);
  const [credentialsError, setCredentialsError] = useState(false);
  const [credentialsExpanded, setCredentialsExpanded] = useState(false);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [savingCredentials, setSavingCredentials] = useState(false);
  const [credentialsMessage, setCredentialsMessage] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const getSatelliteList = useCallback(async () => {
    setListError(false);
    try {
      const response = await api.get("/admin/satellite-images");
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setListError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const getSentinelCredentials = useCallback(async () => {
    setCredentialsLoading(true);
    setCredentialsError(false);
    try {
      const response = await api.get("/admin/sentinel-credentials");
      const status = response.data?.data || EMPTY_CREDENTIAL_STATUS;
      setCredentialsStatus(status);
      setClientId(status.client_id || "");
      if (!status.configured || !status.ready) setCredentialsExpanded(true);
    } catch (error) {
      setCredentialsError(true);
    } finally {
      setCredentialsLoading(false);
    }
  }, []);

  useEffect(() => {
    getSatelliteList();
    getSentinelCredentials();
  }, [getSatelliteList, getSentinelCredentials]);

  const uniqueUsers = useMemo(
    () => users.map((user) => ({
      id: user.user_id,
      label: user.phone_number
        ? `${user.userName} (${user.phone_number})`
        : user.userName,
    })),
    [users]
  );

  const selectedUserFields = useMemo(
    () => selectedUser === ""
      ? users.flatMap((user) => user.fields || [])
      : users.find((user) => user.user_id == selectedUser)?.fields || [],
    [selectedUser, users]
  );

  const uniqueFields = useMemo(
    () => selectedUserFields.map((field) => ({
      id: field.field_id,
      label: field.field_name,
    })),
    [selectedUserFields]
  );

  const filteredUsers = useMemo(
    () => users
      .filter((user) => selectedUser === "" || user.user_id == selectedUser)
      .map((user) => ({
        ...user,
        fields: (user.fields || []).filter(
          (field) => selectedField === "" || field.field_id == selectedField
        ),
      }))
      .filter((user) => user.fields.length > 0),
    [selectedField, selectedUser, users]
  );

  const filteredFieldCount = filteredUsers.reduce(
    (total, user) => total + user.fields.length,
    0
  );

  const handleUserChange = (event) => {
    setSelectedUser(event.target.value);
    setSelectedField("");
  };

  const getApiErrorMessage = (error) => {
    const key = error.response?.data?.message;
    if (key) return t(key, { defaultValue: t("sentinel_credentials_save_error") });
    return t("sentinel_credentials_save_error");
  };

  const handleSaveCredentials = async (event) => {
    event.preventDefault();
    const nextErrors = {};
    const trimmedClientId = clientId.trim();
    const trimmedClientSecret = clientSecret.trim();

    if (!trimmedClientId) nextErrors.clientId = t("sentinel_client_id_required");
    if (!trimmedClientSecret && !credentialsStatus.secret_configured) {
      nextErrors.clientSecret = t("sentinel_client_secret_required");
    }
    setFieldErrors(nextErrors);
    setCredentialsMessage(null);
    if (Object.keys(nextErrors).length > 0) return;

    setSavingCredentials(true);
    try {
      const response = await api.put("/admin/sentinel-credentials", {
        client_id: trimmedClientId,
        client_secret: trimmedClientSecret,
      });
      const status = response.data?.data || EMPTY_CREDENTIAL_STATUS;
      setCredentialsStatus(status);
      setClientId(status.client_id || trimmedClientId);
      setClientSecret("");
      setShowSecret(false);
      setCredentialsMessage({
        type: "success",
        text: t("sentinel_credentials_saved"),
      });
    } catch (error) {
      setCredentialsMessage({ type: "danger", text: getApiErrorMessage(error) });
    } finally {
      setSavingCredentials(false);
    }
  };

  const statusType = credentialsStatus.configured && credentialsStatus.ready
    ? "is-active"
    : credentialsStatus.configured
      ? "is-battery"
      : "is-inactive";
  const statusLabel = credentialsStatus.configured && credentialsStatus.ready
    ? t("sentinel_credentials_configured")
    : credentialsStatus.configured
      ? t("sentinel_credentials_incomplete")
      : t("sentinel_credentials_status_not_configured");
  const updatedAt = formatStatusDate(
    credentialsStatus.updated_at,
    i18n.resolvedLanguage || i18n.language
  );

  if (loading) {
    return (
      <div className="admin-management-page satellite-management-page">
        <div className="admin-loading-card" role="status" aria-label={t("loading")}>
          <div className="admin-loading-pulse" aria-hidden="true">
            <span />
            <span />
            <span />
            <span />
          </div>
        </div>
      </div>
    );
  }

  return (
    <main className="admin-management-page satellite-management-page container">
      <Row className="admin-management-heading">
        <PageTitle sm="4" subtitle={t("all_satellite_images")} className="text-sm-left" />
        <p className="satellite-admin-subtitle">{t("satellite_admin_subtitle")}</p>
      </Row>

      <section className={`sentinel-settings-card ${credentialsExpanded ? "is-expanded" : ""}`}>
        <div className="sentinel-settings-header">
          <div className="sentinel-settings-heading">
            <span className="sentinel-settings-icon" aria-hidden="true">
              <i className="fas fa-shield-alt" />
            </span>
            <div>
              <h2>{t("sentinel_credentials_title")}</h2>
              <p>{t("sentinel_credentials_description")}</p>
            </div>
          </div>
          <div className="sentinel-settings-header-actions">
            {!credentialsLoading && !credentialsError && (
              <span className={`admin-status ${statusType}`}>{statusLabel}</span>
            )}
            <button
              type="button"
              className="sentinel-settings-toggle"
              aria-expanded={credentialsExpanded}
              aria-controls="sentinel-credentials-panel"
              onClick={() => setCredentialsExpanded((expanded) => !expanded)}
            >
              <span>
                {credentialsExpanded
                  ? t("sentinel_hide_configuration")
                  : t("sentinel_show_configuration")}
              </span>
              <i
                className={`fas fa-chevron-${credentialsExpanded ? "up" : "down"}`}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>

        {credentialsExpanded && (
          <div id="sentinel-credentials-panel" className="sentinel-settings-panel">
            {credentialsLoading ? (
              <div className="sentinel-settings-loading" role="status">
                <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                <span>{t("loading")}</span>
              </div>
            ) : credentialsError ? (
              <div className="sentinel-inline-alert is-danger" role="alert">
                <div>
                  <strong>{t("sentinel_credentials_load_error")}</strong>
                  <span>{t("sentinel_credentials_load_error_help")}</span>
                </div>
                <Button type="button" variant="outline-primary" onClick={getSentinelCredentials}>
                  {t("retry")}
                </Button>
              </div>
            ) : (
              <Form noValidate onSubmit={handleSaveCredentials}>
                <div className="sentinel-security-note">
                  <i className="fas fa-lock" aria-hidden="true" />
                  <span>{t("sentinel_credentials_security_note")}</span>
                </div>

                <div className="sentinel-credentials-meta">
                  <span>
                    {t("sentinel_credentials_source")}: <strong>{t(`sentinel_source_${credentialsStatus.source}`)}</strong>
                  </span>
                  {updatedAt && (
                    <span>{t("sentinel_credentials_updated_at", { date: updatedAt })}</span>
                  )}
                </div>

                <div className="sentinel-form-grid">
                  <Form.Group controlId="sentinelClientId">
                    <Form.Label>{t("sentinel_client_id")}</Form.Label>
                    <Form.Control
                      type="text"
                      value={clientId}
                      autoComplete="off"
                      maxLength={255}
                      aria-invalid={Boolean(fieldErrors.clientId)}
                      aria-describedby={fieldErrors.clientId ? "sentinel-client-id-error" : undefined}
                      onChange={(event) => {
                        setClientId(event.target.value);
                        setFieldErrors((errors) => ({ ...errors, clientId: undefined }));
                      }}
                    />
                    {fieldErrors.clientId && (
                      <div id="sentinel-client-id-error" className="sentinel-field-error">
                        {fieldErrors.clientId}
                      </div>
                    )}
                  </Form.Group>

                  <Form.Group controlId="sentinelClientSecret">
                    <Form.Label>{t("sentinel_client_secret")}</Form.Label>
                    <div className="sentinel-secret-control">
                      <Form.Control
                        type={showSecret ? "text" : "password"}
                        value={clientSecret}
                        autoComplete="new-password"
                        maxLength={4096}
                        placeholder={credentialsStatus.secret_configured
                          ? t("sentinel_secret_unchanged_placeholder")
                          : t("sentinel_secret_placeholder")}
                        aria-invalid={Boolean(fieldErrors.clientSecret)}
                        aria-describedby={fieldErrors.clientSecret
                          ? "sentinel-secret-help sentinel-client-secret-error"
                          : "sentinel-secret-help"}
                        onChange={(event) => {
                          setClientSecret(event.target.value);
                          setFieldErrors((errors) => ({ ...errors, clientSecret: undefined }));
                        }}
                      />
                      <button
                        type="button"
                        className="sentinel-secret-toggle"
                        aria-label={showSecret ? t("sentinel_hide_secret") : t("sentinel_show_secret")}
                        onClick={() => setShowSecret((visible) => !visible)}
                      >
                        <i className={`fas fa-eye${showSecret ? "-slash" : ""}`} aria-hidden="true" />
                      </button>
                    </div>
                    <Form.Text id="sentinel-secret-help">
                      {credentialsStatus.secret_configured
                        ? t("sentinel_secret_unchanged_help")
                        : t("sentinel_secret_required_help")}
                    </Form.Text>
                    {fieldErrors.clientSecret && (
                      <div id="sentinel-client-secret-error" className="sentinel-field-error">
                        {fieldErrors.clientSecret}
                      </div>
                    )}
                  </Form.Group>
                </div>

                <div className="sentinel-form-footer">
                  <div className="sentinel-message" aria-live="polite" aria-atomic="true">
                    {credentialsMessage && (
                      <div
                        className={`sentinel-inline-alert is-${credentialsMessage.type}`}
                        role={credentialsMessage.type === "danger" ? "alert" : "status"}
                      >
                        <i
                          className={`fas fa-${credentialsMessage.type === "success" ? "check-circle" : "exclamation-circle"}`}
                          aria-hidden="true"
                        />
                        <span>{credentialsMessage.text}</span>
                      </div>
                    )}
                  </div>
                  <Button type="submit" variant="primary" disabled={savingCredentials}>
                    {savingCredentials && (
                      <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                    )}
                    <span>
                      {savingCredentials
                        ? t("sentinel_testing_and_saving")
                        : t("sentinel_test_and_save")}
                    </span>
                  </Button>
                </div>
              </Form>
            )}
          </div>
        )}
      </section>

      <section className="admin-management-toolbar" aria-labelledby="satellite-filters-title">
        <div className="admin-management-toolbar-heading">
          <div id="satellite-filters-title" className="admin-management-toolbar-title">
            <i className="fas fa-filter" aria-hidden="true" />
            {t("filters", { defaultValue: "Filtres" })}
          </div>
          <span className="satellite-result-count">
            {t("satellite_fields_result_count", { count: filteredFieldCount })}
          </span>
        </div>
        <div className="admin-management-filters">
          <div>
            <Form.Label htmlFor="satellite-user-filter">{t("select_user")}</Form.Label>
            <select
              id="satellite-user-filter"
              className="form-select"
              value={selectedUser}
              onChange={handleUserChange}
            >
              <option value="">{t("all_users")}</option>
              {uniqueUsers.map((user) => (
                <option key={user.id} value={user.id}>{user.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Form.Label htmlFor="satellite-field-filter">{t("select_field")}</Form.Label>
            <select
              id="satellite-field-filter"
              className="form-select"
              value={selectedField}
              onChange={(event) => setSelectedField(event.target.value)}
            >
              <option value="">{t("all_fields")}</option>
              {uniqueFields.map((field) => (
                <option key={field.id} value={field.id}>{field.label}</option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <p className="admin-management-note">{t("users_with_fields_only")}</p>

      {listError ? (
        <div className="satellite-list-error" role="alert">
          <i className="fas fa-satellite-dish" aria-hidden="true" />
          <div>
            <strong>{t("satellite_admin_list_error")}</strong>
            <span>{t("satellite_admin_list_error_help")}</span>
          </div>
          <Button type="button" variant="outline-primary" onClick={getSatelliteList}>
            {t("retry")}
          </Button>
        </div>
      ) : (
        <Card className="admin-data-card satellite-data-card">
          <Card.Body>
            <div className="table-responsive">
              <table className="table text-center align-middle satellite-images-table">
                <thead className="bg-light">
                  <tr>
                    <th scope="col">{t("user_name")}</th>
                    <th scope="col">{t("field_name")}</th>
                    <th scope="col">{t("images")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFieldCount === 0 && (
                    <tr>
                      <td colSpan="3" className="admin-empty-row">{t("no_data_available")}</td>
                    </tr>
                  )}
                  {filteredUsers.map((user) => user.fields.map((field, index) => (
                    <tr key={`${user.user_id}-${field.field_id}`}>
                      {index === 0 && (
                        <td rowSpan={user.fields.length} className="satellite-user-cell">
                          {user.userName}
                        </td>
                      )}
                      <td>{field.field_name || t("unnamed_field")}</td>
                      <td>
                        <Button
                          type="button"
                          variant="primary"
                          size="sm"
                          onClick={() => navigate(`${field.field_uid}`)}
                        >
                          <i className="fas fa-images" aria-hidden="true" />
                          <span>{t("view_all_images")}</span>
                        </Button>
                      </td>
                    </tr>
                  )))}
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>
      )}
    </main>
  );
}
